import { Injectable, Signal } from '@angular/core';

import { Store, select } from '@ngrx/store';
import { Observable, filter, of, switchMap, tap } from 'rxjs';

import { TiimeSnackbarService } from 'tiime-material';

import { AccountingConfigApiService } from '@api-services/accounting-config-api.service';
import { AppStoreState } from '@interfaces/app-store-state';
import { AccountingConfig } from '@models/accounting-config';
import { ConfirmDialogData } from '@shared-dialogs/confirm-dialog/confirm-dialog-data';
import { ConfirmDialogService } from '@shared-dialogs/confirm-dialog/confirm-dialog.service';

import { updatePartial } from './accounting-config-actions';
import { accountingConfigSelector } from './accounting-config-selector';

@Injectable({
  providedIn: 'root'
})
export class AccountingConfigStore {
  /**
   * Un observable qui se déclenche à chaque fois qu'une propriété de l'accounting config est mise à jour.
   */
  readonly accountingConfig$: Observable<AccountingConfig> = this.store.pipe(select(accountingConfigSelector));

  /**
   * Le signal correspondant à l'accounting config.
   */
  readonly accountingConfig: Signal<AccountingConfig> = this.store.selectSignal(accountingConfigSelector);

  constructor(
    private readonly store: Store<AppStoreState>,
    private readonly confirmDialogService: ConfirmDialogService,
    private readonly snackbar: TiimeSnackbarService,
    private readonly accountingConfigApiService: AccountingConfigApiService
  ) {}

  /**
   * Méthode qui permet de supprimer la période personnalisée
   * @param {boolean} confirm afficher la modale de confirmation
   * @returns {AccountingConfig}
   */
  deleteCustomAccountingPeriod(confirm = true): Observable<AccountingConfig> {
    const obs = confirm ? this.openConfirmDeleteCustomAccountingPeriod() : of(true);

    return obs.pipe(
      switchMap(() =>
        this.accountingConfigApiService.updatePartialAccountingConfig({
          id: this.accountingConfig().id,
          custom_accounting_period_end_date: null
        })
      ),
      tap(() => {
        this.store.dispatch(updatePartial({ accountingConfig: { customAccountingPeriodEndDate: null } }));
        if (confirm) {
          this.snackbar.openSuccess('La période personalisée a bien été supprimée');
        }
      })
    );
  }

  private openConfirmDeleteCustomAccountingPeriod(): Observable<boolean> {
    const data: ConfirmDialogData = new ConfirmDialogData();
    data.confirmMessage = `Êtes-vous sûr de vouloir supprimer la période personalisée ?`;
    data.confirmAction = 'Supprimer';
    data.actionButtonColor = 'warn';

    return this.confirmDialogService
      .open({ data })
      .afterClosed()
      .pipe(filter((confirm: boolean) => confirm));
  }
}
